import {
  Box,
  Button,
  Flex,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import { ArticleNavigationContext } from '@bridge/navigationStore';
import { reactEvents } from '@bridge/reactEvents';
import { Icons } from '@texas/components/shared/Icons';
import { useTranslation } from 'react-i18next';
import { getVariantLandingRoute } from './navigationItems';

interface ArticleVariantPickerProps {
  context: ArticleNavigationContext;
  selectedVariantId: number;
}

export function ArticleVariantPicker({
  context,
  selectedVariantId,
}: ArticleVariantPickerProps) {
  const { t } = useTranslation();
  const currentVariant = context.variants.find(
    (variant) => variant.id === selectedVariantId,
  );
  const mainVariant = context.variants.find((variant) => variant.isMain);
  const variants = context.variants
    .filter((variant) => !variant.archived && !variant.isProductDevelopment)
    .sort((a, b) => Number(b.isMain) - Number(a.isMain));

  if (!currentVariant) {
    return null;
  }

  return (
    <Box>
      <Menu placement="bottom-start">
        <MenuButton
          as={Button}
          rightIcon={<Icons.ChevronDown boxSize={4} />}
          variant="navigationVariant"
        >
          <Text noOfLines={1}>
            {currentVariant.name}
            {currentVariant.archived ? ` (${t('navigation.archived')})` : ''}
          </Text>
        </MenuButton>
        <MenuList minW="220px">
          <MenuItem
            icon={<Icons.Plus boxSize={4} />}
            onClick={() => reactEvents.newArticleVariantRequested.dispatch()}
          >
            {t('navigation.addVariant')}
          </MenuItem>
          <MenuDivider />
          {variants.map((variant) => (
            <MenuItem
              key={variant.id}
              icon={
                variant.optionLinkId !== null || variant.isMain ? (
                  <Flex align="center" gap={1}>
                    {variant.optionLinkId !== null && (
                      <Tooltip label={t('garp.linkedOption')}>
                        <Icons.Link boxSize={4} />
                      </Tooltip>
                    )}
                    {variant.isMain && (
                      <Tooltip label={t('variant.isMain')}>
                        <Icons.Flag boxSize={4} />
                      </Tooltip>
                    )}
                  </Flex>
                ) : undefined
              }
              fontWeight={variant.id === currentVariant.id ? 'bold' : 'normal'}
              onClick={() =>
                reactEvents.routingChanged.dispatch({
                  to: getVariantLandingRoute(variant.isProductDevelopment),
                  navObj: {
                    articleId: context.article.id,
                    variantId: variant.id,
                  },
                })
              }
            >
              {variant.name}
            </MenuItem>
          ))}
        </MenuList>
      </Menu>
      {mainVariant && (
        <Text
          color="navigation.contextMuted"
          fontSize="11px"
          mt={1}
          noOfLines={1}
          px={2.5}
        >
          {t('navigation.mainVariant', { name: mainVariant.name })}
        </Text>
      )}
    </Box>
  );
}
